// Records as rows (Carbon DataTable, JAL Core chrome). Two renderings of
// the same rows, one shown per width, so the page never scrolls sideways:
//   below 640   stacked records: the title column as a 16/500 line, up to
//               three meta columns as a 13 line, one trailing cell (a badge)
//   640 and up  a real <table> in its own scroll box: the header row is
//               sticky inside the box, wide tables scroll horizontally
//               inside it, numbers are right aligned in tabular figures, row
//               height follows the product density from 1024
import { type ReactNode } from "react";

export interface DataColumn<Row> {
  key: string;
  header: string;
  /** Cell content. Default: String(row[key]). */
  cell?: (row: Row) => ReactNode;
  /** Right-align in tabular figures. */
  numeric?: boolean;
  /**
   * Let this column wrap (long free text such as a programme name). Every
   * other column stays on one line, so rows keep one height and the table
   * scrolls inside its own box when it is wider than the panel.
   */
  wrap?: boolean;
  /**
   * Role in the stacked record below 640:
   * "title" (the first column by default), "meta" (the next three by default),
   * "trailing" (a badge or status), or "hidden".
   */
  mobile?: "title" | "meta" | "trailing" | "hidden";
}

export interface DataTableProps<Row> {
  /** Names the table for assistive tech and sits above it as its caption. */
  caption: string;
  columns: DataColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** Shown in place of the rows when there are none (an EmptyState). */
  empty?: ReactNode;
  /** Range text or pagination under the table, e.g. "Menampilkan 8 dari 300". */
  footer?: ReactNode;
  /** Scroll box height cap; the header stays visible while rows scroll. */
  height?: "sm" | "md" | "lg";
}

function read<Row>(row: Row, col: DataColumn<Row>): ReactNode {
  if (col.cell) return col.cell(row);
  const v = (row as Record<string, unknown>)[col.key];
  return v === null || v === undefined ? "" : String(v);
}

function roles<Row>(columns: DataColumn<Row>[]) {
  const title = columns.find((c) => c.mobile === "title") ?? columns.find((c) => c.mobile === undefined) ?? columns[0];
  const trailing = columns.filter((c) => c.mobile === "trailing");
  const explicitMeta = columns.filter((c) => c.mobile === "meta");
  const meta =
    explicitMeta.length > 0
      ? explicitMeta
      : columns.filter((c) => c !== title && c.mobile === undefined).slice(0, 3);
  return { title, meta, trailing };
}

export function DataTable<Row>({ caption, columns, rows, rowKey, empty, footer, height = "md" }: DataTableProps<Row>) {
  const { title, meta, trailing } = roles(columns);
  if (rows.length === 0 && empty) {
    return (
      <div className="con-table" data-empty="">
        <p className="con-table-caption">{caption}</p>
        {empty}
      </div>
    );
  }
  return (
    <div className="con-table">
      <ul className="con-records" aria-label={caption}>
        {rows.map((row) => (
          <li key={rowKey(row)} className="con-record">
            <div className="con-record-text">
              <p className="con-record-title">{read(row, title)}</p>
              {meta.length ? (
                <p className="con-record-meta">
                  {meta.map((c) => (
                    <span key={c.key}>
                      <span className="con-sr">{c.header}: </span>
                      {read(row, c)}
                    </span>
                  ))}
                </p>
              ) : null}
            </div>
            {trailing.length ? (
              <div className="con-record-trailing">
                {trailing.map((c) => (
                  <span key={c.key}>{read(row, c)}</span>
                ))}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      <div className="con-table-scroll" data-height={height} tabIndex={0} role="region" aria-label={caption}>
        <table aria-label={caption}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} scope="col" data-numeric={c.numeric ? "" : undefined} data-wrap={c.wrap ? "" : undefined}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={rowKey(row)}>
                {columns.map((c) => (
                  <td key={c.key} data-numeric={c.numeric ? "" : undefined} data-wrap={c.wrap ? "" : undefined}>
                    {read(row, c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer ? <div className="con-table-footer">{footer}</div> : null}
    </div>
  );
}
